import { SessionQuery } from 'src/app/core/state/session/session.query';
import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { createPopper, Instance } from '@popperjs/core';
import { PopoverDirective } from './popover/popover.directive';
import {
    BehaviorSubject,
    combineLatest,
    EMPTY,
    fromEvent,
    merge,
    Observable,
    of,
    Subject
} from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    share,
    shareReplay,
    skip,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import { Menu, MenuItem } from './interfaces/menu';
import { SbMenuService, SbWidgetService } from '../../legacy/services';
import { SbWidgetContext } from '@models';
import { AppConfigQuery } from '../state/app-config/app-config.query';
import { KeycloakService } from 'keycloak-angular';
import { ImpersonateQuery } from '../../pages/admin/impersonate/state/impersonate.query';
import {
    fadeInOut,
    list,
    slideInOut
} from '../../shared/animations/fade-in-out.animation';

@Component({
    selector: 'app-sb-menu',
    templateUrl: './sb-menu.component.html',
    styleUrls: ['./sb-menu.component.scss'],
    animations: [fadeInOut, list, slideInOut]
})
export class SbMenuComponent implements OnInit, OnDestroy {
    // TODO: remove!
    // Hard coded value for the account selector widget. Though somewhat ugly it doesn't have to be configurable at this stage.
    private readonly accountSelectorWidgetId =
        'root-account-selection-widget-with-backend';
    accountMultiSelectModel: SbWidgetContext;
    enableAccountSelector = false; //this.appConfig.get().useGlobalAccountSelector || false;

    isTouchDevice = false;
    _popperInstance: Instance;
    unsubscribe$ = new Subject();
    $activeMenuItem: BehaviorSubject<MenuItem | false> = new BehaviorSubject<
        MenuItem | false
    >(false);
    $isOpen = this.$activeMenuItem.pipe(
        map((value) => !!value),
        distinctUntilChanged(),
        share()
    );
    isAdminUser$ = this.appConfig.appConfig.useLatestKeycloakFlow
        ? of(this.keycloak.isUserInRole('ROLE_ADMINISTRATOR'))
        : this.sessionQuery.activeUserRoles$.pipe(
              map((a) => a?.some((r) => r === 'ROLE_ADMINISTRATOR')),
              shareReplay(1)
          );

    startPage$ = this.sessionQuery.decodedToken$.pipe(
        map((_) => {
            return [
                // if user is admin, redirect to admin page
                this.sessionQuery.isAdminUser
                    ? '/admin'
                    : // if user is asset manager and not impersonating another user, redirect to impersonation page
                    this.sessionQuery.isAssetManager &&
                      !this.impersonateQuery.activeImpersonation
                    ? '/impersonation'
                    : // if user is not admin or asset manager, redirect to landing page
                      this.appConfig.appConfig.landingPageOverride || ''
            ];
        }),
        shareReplay(1)
    );

    isAuthenticated$ = this.sessionQuery.isAuthenticated$;
    impersonateUser$ = this.impersonateQuery.selectImpersonatedUser$;
    constructor(
        private cdr: ChangeDetectorRef,
        private menuService: SbMenuService,
        private sbWidgetService: SbWidgetService,
        private appConfig: AppConfigQuery,
        private sessionQuery: SessionQuery,
        private keycloak: KeycloakService,
        private impersonateQuery: ImpersonateQuery
    ) {
        this.sbWidgetService.resolveContext(this.accountSelectorWidgetId).then((w) => {
            this.accountMultiSelectModel = w;
            this.isAdminUser$
                .pipe(
                    tap((isAdmin) => {
                        this.enableAccountSelector = isAdmin
                            ? false
                            : this.accountMultiSelectModel && false; // this.appConfig.get().useGlobalAccountSelector;
                    }),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe();
        });
    }
    $adminMenu: Observable<Menu> = of([
        {
            label: 'Accounts',
            type: 'MENU_ITEM',
            icon: 'wallet',
            index: 0,
            routerLink: ['/admin/accounts'],
            accessRights: ['ADMINISTORATOR']
        },
        {
            label: 'Issuers',
            type: 'MENU_ITEM',
            icon: 'building-bank',
            index: 1,
            routerLink: ['/admin/issuers'],
            accessRights: ['ADMINISTORATOR']
        },
        {
            label: 'Categories',
            type: 'MENU_ITEM',
            icon: 'category-2',
            index: 2,
            routerLink: ['/categories'],
            accessRights: ['GLOBAL_CATEGORY_ADMIN', 'TENANT_CATEGORY_ADMIN']
        },
        {
            label: 'Market data',
            type: 'MENU_ITEM',
            icon: 'world',
            index: 3,
            accessRights: ['VIEW_PRICE_GUI'],
            items: [
                {
                    label: 'Securities',
                    icon: 'list',
                    index: 0,
                    routerLink: ['/sb-grid/marketdata-list-securities'],
                    accessRights: ['CREATE_PUBLIC_INSTRUMENT']
                },
                {
                    label: 'Prices',
                    icon: 'list',
                    index: 0,
                    routerLink: ['/sb-grid/marketdata-list-prices'],
                    accessRights: ['CREATE_PUBLIC_INSTRUMENT']
                },
                {
                    label: 'Shares',
                    icon: 'list',
                    index: 0,
                    routerLink: ['/sb-grid/marketdata-list-total-shares'],
                    accessRights: ['CREATE_PUBLIC_INSTRUMENT']
                }
            ]
        },
        {
            label: 'Legal entities',
            type: 'MENU_ITEM',
            icon: 'affiliate',
            index: 1,
            routerLink: ['/admin/owners'],
            accessRights: ['ADMINISTORATOR']
        },
        {
            label: 'Users',
            type: 'MENU_ITEM',
            icon: 'users',
            index: 1,
            routerLink: ['/admin/users-and-permissions'],
            accessRights: ['ADMINISTORATOR']
        }
    ]);

    $menu = combineLatest([
        this.isAdminUser$,
        this.impersonateQuery.selectImpersonatedUser$
    ]).pipe(
        switchMap(([isAdmin, impersonatedUser]) =>
            isAdmin && !impersonatedUser ? this.$adminMenu : this.menuService.getMenu()
        ),
        map((menu) =>
            menu.filter(
                (menuItem) => !menuItem.routerLink || menuItem?.type === 'MENU_ITEM'
            )
        )
    );

    $actions = this.isAdminUser$.pipe(
        switchMap((isAdmin) => (isAdmin ? this.$adminMenu : this.menuService.getMenu())),
        map((menu) =>
            menu.filter(
                (menuItem) => menuItem.routerLink && menuItem?.type !== 'MENU_ITEM'
            )
        )
    );
    @ViewChild(PopoverDirective) menuElement: PopoverDirective;

    toggle(menuItem: MenuItem, menuElement: HTMLLIElement, $event: MouseEvent) {
        if (this.$activeMenuItem.value === menuItem) {
            this.close();
        } else {
            this.open(menuItem, menuElement, $event);
        }
    }

    close() {
        this.$activeMenuItem.next(false);
        // clear popper instance
        this._popperInstance?.destroy();
        this._popperInstance = null;
    }
    open(menuItem: MenuItem, menuItemElement: HTMLLIElement, $event: MouseEvent) {
        this.$activeMenuItem.next(menuItem);
        // if we have a popper instance...
        if (this._popperInstance) {
            // ...update popper reference i.e. element popper is attached to
            this._popperInstance.state.elements.reference = menuItemElement;
            this._popperInstance.update().then((_) => this.cdr.detectChanges());
        } else {
            // ...if not, create a new popper instance
            this._popperInstance = createPopper(
                menuItemElement,
                this.menuElement.elRef.nativeElement,
                {
                    placement: 'bottom-start'
                }
            );
        }
        // trigger update of popper position
        this.cdr.detectChanges();
    }

    handleClickEvent(event: Event) {
        // if click inside popover element...
        if (this.menuElement.elRef.nativeElement.contains(event.target)) {
            this.close();
        } else if (this.$activeMenuItem.value) {
            // else if click outside popover element...
            this.close();
        }
    }
    handleKeydownEvent(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            // close menu
            this.close();
            return;
        } else if (event.key === 'End') {
            // put focus on last menu item
        } else if (event.key === 'Home') {
            // put focus on first menu item
        } else if (event.key === 'ArrowUp') {
            // move focus to previous item
        } else if (event.key === 'ArrowDown') {
            // move focus to next item
        }
    }

    ngOnInit(): void {
        // check if touch event occurs and mark device as touch device to prevent
        // mouseenter and click from emitting on click i.e. prevent double event for menu open
        /* TODO: detect if click event is preceded by touch start to allow switching between
        touch and mouse events i.e. laptop with touch screen. Current "fix" locks behavior
        to touch once a touch event occurs.
    */
        fromEvent(document, 'touchstart')
            .pipe(
                tap((_) => (this.isTouchDevice = true)),
                take(1) // unsubscribe on first emit
            )
            .subscribe();

        // subscribe to menu open
        this.$isOpen
            .pipe(
                switchMap(
                    (isOpen) =>
                        isOpen // if open...
                            ? merge(
                                  // ...listen to click events
                                  fromEvent(document, 'click').pipe(
                                      skip(1),
                                      tap((event) => this.handleClickEvent(event))
                                  ),
                                  // ...listen to keydown events
                                  fromEvent(document, 'keydown').pipe(
                                      // ...filter keys
                                      filter(
                                          (event) =>
                                              [
                                                  'ArrowDown',
                                                  'ArrowUp',
                                                  'Escape',
                                                  'Home',
                                                  'End'
                                              ].indexOf((event as KeyboardEvent).key) !==
                                              -1
                                      ),
                                      tap((event) =>
                                          this.handleKeydownEvent(event as KeyboardEvent)
                                      )
                                  )
                              )
                            : EMPTY // if closed do nothing
                ),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
